/**
 * Zweistufiger Filter der Uebertragungs-Gesamtuebersicht (D-03, D-13, D-21; Sketch 025
 * `filterBlock()`/`wireFilter()`): Stufe 1 Bereich als `SegmentedControl`, Stufe 2 -- nur bei
 * gewaehltem Bereich mit Auswertungen -- die Auswertungen als `Chip.Group`. Unfalldaten haben keine
 * Auswertungen, deshalb entfaellt dort die zweite Stufe (D-13). Hat eine Person nur einen lesbaren
 * Bereich, entfaellt die Bereichsauswahl ganz (D-21) -- der Server setzt den Bereich dann.
 * Anzahlen je Bereich/Auswertung zeigt der Filter bewusst nicht (Nutzerentscheidung 2026-09-29).
 */
import { Chip, Group, SegmentedControl, Stack } from '@mantine/core';
import type {
  TransferArea,
  TransferAreaOption,
  TransferTypeOption,
} from '../../types/statistikPortalTransfer';
import classes from './TransferFilter.module.css';

export interface TransferFilterProps {
  areas: TransferAreaOption[];
  area: TransferArea | null;
  evaluationTypes: TransferTypeOption[];
  type: string | null;
  onAreaChange: (area: TransferArea | null) => void;
  onTypeChange: (type: string | null) => void;
}

export function TransferFilter({
  areas,
  area,
  evaluationTypes,
  type,
  onAreaChange,
  onTypeChange,
}: TransferFilterProps) {
  const showAreaSegment = areas.length > 1;
  const showTypeChips = area !== null && evaluationTypes.length > 0;
  const currentAreaLabel = areas.find((candidate) => candidate.key === area)?.label ?? '';

  return (
    <Stack gap="sm">
      {showAreaSegment && (
        <Group>
          <SegmentedControl
            value={area ?? ''}
            onChange={(value) => onAreaChange(value === '' ? null : (value as TransferArea))}
            data={[
              { value: '', label: 'Alle' },
              ...areas.map((a) => ({ value: a.key, label: a.label })),
            ]}
            aria-label="Bereich"
            color="vdzGray"
            classNames={{ root: classes.segmentRoot }}
          />
        </Group>
      )}
      {showTypeChips && (
        <Chip.Group
          value={type ?? ''}
          onChange={(value) => onTypeChange(value === '' ? null : (value as string))}
        >
          <Group gap="xs">
            <Chip value="" classNames={{ label: classes.chipLabel }}>
              Alle {currentAreaLabel}
            </Chip>
            {evaluationTypes.map((t) => (
              <Chip key={t.key} value={t.key} classNames={{ label: classes.chipLabel }}>
                {t.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      )}
    </Stack>
  );
}
